
import { useEffect } from 'react';
import { Header } from '../../../components/feature/Header';
import { Footer } from '../../../components/feature/Footer';
import InheritedApartmentsHero from './components/InheritedApartmentsHero';
import InheritanceComplexities from './components/InheritanceComplexities';
import ProbateProcess from './components/ProbateProcess';
import MultipleHeirsChallenges from './components/MultipleHeirsChallenges';
import CarryingCostsBreakdown from './components/CarryingCostsBreakdown';
import WhySpeedMatters from './components/WhySpeedMatters';
import OurInheritedPropertyProcess from './components/OurInheritedPropertyProcess';
import TaxConsiderations from './components/TaxConsiderations';
import RealInheritanceStories from './components/RealInheritanceStories';
import LegalDocumentChecklist from './components/LegalDocumentChecklist';
import InheritedPropertyFAQ from './components/InheritedPropertyFAQ';
import InheritedPropertyFinalCTA from './components/InheritedPropertyFinalCTA';

const formUrl = 'https://readdy.ai/api/form/d5qqu4otu5df1co4dbmg';

export default function SellingInheritedApartmentsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Set page title and meta tags using React 19 features
    document.title = 'Selling Inherited Apartment Buildings: Complete Guide to Probate, Taxes & Fast Sale';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Navigate probate, multiple heirs, and carrying costs when selling inherited apartments. Get fair cash offers, avoid repairs, and close fast. Expert guidance for complex inheritance situations.');
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'selling inherited apartment building, inherited property probate, sell inherited apartments fast, inherited real estate taxes, multiple heirs property sale, probate property sale, inherited apartment building cash offer');
    }
    
    // Add structured data for Article
    const existingArticleScript = document.querySelector('script[type="application/ld+json"][data-type="article"]');
    if (!existingArticleScript) {
      const articleScript = document.createElement('script');
      articleScript.type = 'application/ld+json';
      articleScript.setAttribute('data-type', 'article');
      articleScript.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": "Selling Inherited Apartment Buildings: Complete Guide to Probate, Taxes & Fast Sale",
        "description": "Comprehensive guide to selling inherited apartment buildings, covering probate process, tax implications, multiple heir coordination, and fast sale options.",
        "author": {
          "@type": "Organization",
          "name": "Your Company Name"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Your Company Name"
        },
        "datePublished": "2024-01-15",
        "dateModified": new Date().toISOString().split('T')[0]
      });
      document.head.appendChild(articleScript);
    }
    
    // Add structured data for FAQ
    const existingFAQScript = document.querySelector('script[type="application/ld+json"][data-type="faq"]');
    if (!existingFAQScript) {
      const faqScript = document.createElement('script');
      faqScript.type = 'application/ld+json';
      faqScript.setAttribute('data-type', 'faq');
      faqScript.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "Can I sell an inherited apartment building before probate is complete?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "In most states, you cannot legally transfer title until probate court approves the sale. However, you can accept offers and prepare for closing during probate. We work with your attorney to expedite court approval and close quickly once authorized."
            }
          },
          {
            "@type": "Question",
            "name": "What are the tax implications of selling inherited property?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "You receive a step-up in basis to fair market value at date of death, often eliminating capital gains tax. However, tax situations vary. We recommend consulting a CPA and can connect you with professionals who specialize in inherited property."
            }
          },
          {
            "@type": "Question",
            "name": "Do all heirs need to agree to sell?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Generally yes, unless the will or trust gives one person sole authority. We've helped many families reach consensus by providing fair market analysis and explaining the costs of delay."
            }
          }
        ]
      });
      document.head.appendChild(faqScript);
    }
    
    // Cleanup function to remove scripts when component unmounts
    return () => {
      const articleScript = document.querySelector('script[data-type="article"]');
      const faqScript = document.querySelector('script[data-type="faq"]');
      if (articleScript) articleScript.remove();
      if (faqScript) faqScript.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        <InheritedApartmentsHero />
        <InheritanceComplexities />
        <ProbateProcess />
        <MultipleHeirsChallenges />
        <CarryingCostsBreakdown />
        <WhySpeedMatters />
        <OurInheritedPropertyProcess />
        <TaxConsiderations />
        <RealInheritanceStories />
        <LegalDocumentChecklist />
        <InheritedPropertyFAQ />
        <InheritedPropertyFinalCTA formUrl={formUrl} />
      </main>
      <Footer />
    </div>
  );
}
